import { useState, useEffect, useCallback } from "react";
import client, { apiErr } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { ChevronDown, ChevronRight, Layers, Search, Building2, MapPin } from "lucide-react";
import { toast } from "sonner";
import { usePoll } from "@/hooks/usePoll";
import { hoursToHM } from "@/lib/duration";
import { PeriodBar, rangeFor, ActiveDot, localISO } from "@/components/PeriodBar";
import { useActiveWorkers } from "@/hooks/useActiveWorkers";
import { mergeStaff } from "@/lib/mergeStaff";
import { HoursBySite } from "@/components/HoursBySite";

const today = () => localISO();
const hrs = (v) => (v ? hoursToHM(v) : "—");

export default function CombinedTimesheets() {
  const { t } = useI18n();
  const activeIds = useActiveWorkers();
  const [date, setDate] = useState(today());
  const [mode, setMode] = useState("day");
  const { start, end } = rangeFor(date, mode);
  const [data, setData] = useState({ workers: [], grandTotal: 0, grandSite: 0, grandField: 0 });
  const [open, setOpen] = useState({});
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [view, setView] = useState("cleaner");
  useEffect(() => { client.get("/users").then((r) => setUsers(r.data)).catch(() => {}); }, []);

  const load = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    client.get(`/timesheets/combined?start=${start}&end=${end}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiErr(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [start, end]);
  useEffect(() => { load(); }, [load]);
  usePoll(() => load(true), 20000);

  const s = q.trim().toLowerCase();
  const workers = mergeStaff(data.workers, users, "workerId", "name", { totalSite: 0, totalField: 0, totalBreaks: 0, total: 0 })
    .filter((w) => !s || (w.name || "").toLowerCase().includes(s));

  return (
    <div className="space-y-6" data-testid="combined-timesheets-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-display font-bold text-slate-900 flex items-center gap-2"><Layers className="h-6 w-6 text-brand" />{t("nav.allHours", "All Hours")}</h1>
          <p className="text-sm text-slate-500">{t("comb.subtitle", "Every worked hour in one place — static-site attendance (net of breaks) plus field job & workday time. Breaks are shown but not counted. No pay amounts.")}</p>
        </div>
        <div className="flex-1 min-w-[280px] max-w-md">
          <PeriodBar date={date} mode={mode} setDate={setDate} setMode={setMode} testid="comb-period" />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="flex rounded-lg border border-slate-200 p-0.5 bg-white" data-testid="comb-view-toggle">
          {[["cleaner", "By cleaner"], ["site", "By site"]].map(([k, l]) => (
            <Button key={k} size="sm" variant="ghost" onClick={() => setView(k)} data-testid={`comb-view-${k}`}
              className={view === k ? "bg-brand text-white hover:bg-brand-dark hover:text-white" : "text-slate-600"}>{l}</Button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="comb-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("ts.search", "Search worker…")} className="pl-9 h-9" />
        </div>
        {view === "cleaner" && <div className="flex flex-wrap gap-2" data-testid="comb-grand-totals">
          <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-200"><Building2 className="h-3 w-3 mr-1" />{t("comb.site", "Site")} {hoursToHM(data.grandSite || 0)}</Badge>
          <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200"><MapPin className="h-3 w-3 mr-1" />{t("comb.field", "Field")} {hoursToHM(data.grandField || 0)}</Badge>
          <Badge variant="outline" className="bg-emerald-600 text-white border-emerald-700 font-semibold">{t("comb.total", "Total")} {hoursToHM(data.grandTotal || 0)}</Badge>
        </div>}
      </div>

      {view === "site" ? <HoursBySite start={start} end={end} q={s} /> : loading ? <p className="text-slate-400">{t("common.loading", "Loading…")}</p> : workers.length === 0 ? (
        <Card className="p-8 text-center text-slate-400" data-testid="comb-empty">{t("comb.empty", "No hours recorded for this period.")}</Card>
      ) : workers.map((w) => {
        const isOpen = open[w.workerId];
        return (
          <Card key={w.workerId} className="overflow-hidden border-slate-200" data-testid={`comb-worker-${w.workerId}`}>
            <div className="flex items-center justify-between px-4 py-3 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => setOpen((o) => ({ ...o, [w.workerId]: !o[w.workerId] }))}>
              <div className="flex items-center gap-3 flex-wrap">
                {isOpen ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
                <span className="h-9 w-9 shrink-0 rounded-full bg-slate-800 text-white text-xs font-semibold flex items-center justify-center">{(w.name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0].toUpperCase()).join("")}</span>
                <span className="font-semibold text-slate-900 flex items-center gap-1.5"><ActiveDot show={activeIds.has(w.workerId)} />{w.name}</span>
                {(activeIds.has(w.workerId) || (w.days || []).some((d) => d.live)) && (
                  <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700" data-testid={`comb-running-${w.workerId}`}>
                    <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />{t("ts.timerRunning", "Timer running")}
                  </span>
                )}
                <Badge variant="outline" className="bg-teal-50 text-teal-700 border-teal-200">{t("comb.site", "Site")} {hrs(w.totalSite)}</Badge>
                <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200">{t("comb.field", "Field")} {hrs(w.totalField)}</Badge>
                {w.totalBreaks > 0 && <Badge variant="outline" className="bg-slate-100 text-slate-500">{t("comb.breaks", "Breaks")} {hrs(w.totalBreaks)} {t("comb.notCounted", "(not counted)")}</Badge>}
              </div>
              <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white font-semibold" data-testid={`comb-total-${w.workerId}`}>{w.total}h {t("comb.total", "total")}</Badge>
            </div>
            {isOpen && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-[11px] uppercase text-slate-400 bg-white">
                    <th className="px-3 py-2">{t("ts.col.date", "date")}</th>
                    <th className="px-3 py-2">{t("comb.site", "Site")}</th>
                    <th className="px-3 py-2">{t("comb.field", "Field")}</th>
                    <th className="px-3 py-2">{t("comb.breaks", "Breaks")}</th>
                    <th className="px-3 py-2 text-right">{t("comb.total", "Total")}</th>
                  </tr></thead>
                  <tbody>
                    {w.days.map((d) => (
                      <tr key={d.date} data-testid={`comb-day-${w.workerId}-${d.date}`} className="border-t border-slate-50">
                        <td className="px-3 py-2 whitespace-nowrap">{d.date}{d.live && <span className="ml-1.5 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700" data-testid={`comb-live-${w.workerId}-${d.date}`}><span className="h-1.5 w-1.5 rounded-full bg-emerald-500 animate-pulse" />Live</span>}</td>
                        <td className="px-3 py-2 text-teal-700">{hrs(d.siteHours)}</td>
                        <td className="px-3 py-2 text-orange-700">{hrs(d.fieldHours)}{d.hasWorkday && d.jobTime > 0 ? <span className="text-[11px] text-slate-400"> · {t("comb.jobPart", "incl.")} {hoursToHM(d.jobTime)} {t("comb.jobs", "jobs")}</span> : null}</td>
                        <td className="px-3 py-2 text-slate-400">{d.siteBreaks > 0 ? hoursToHM(d.siteBreaks) : "—"}</td>
                        <td className="px-3 py-2 text-right font-semibold text-emerald-700">{hrs(d.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}
